import React, { useEffect } from 'react';
import { da, fi, sv, nb, nl, enGB } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { LANGUAGES_CODE } from 'utils/constants';

// ─── Locale helpers ───────────────────────────────────────────────────────────

// Aliases mirror utils/i18n/moment.js so locale resolution is consistent
// across calendar components (navigator/grid panels).
const LOCALE_MAP: Record<string, Locale> = {
    da,
    fi,
    sv,
    se: sv,
    nb,
    no: nb,
    nl,
};

export const getDateFnsLocale = (langCode: string): Locale => {
    const base = LOCALE_MAP[langCode] ?? enGB;
    return { ...base, options: { ...base.options, weekStartsOn: 1 as const } };
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

export const buildRange = (start: Date, end: Date) => ({ startDate: start, endDate: end, key: 'date' });

export const formatDate = (date: Date, langCode: string): string =>
    date.toLocaleDateString(LANGUAGES_CODE[langCode] ?? 'en-GB');

export const formatDisplayValue = (start: Date, end: Date, langCode: string): string => {
    const s = formatDate(start, langCode);
    const e = formatDate(end, langCode);
    return start.getTime() === end.getTime() ? s : `${s} / ${e}`;
};

/** Compare two dates by year+month only */
export const sameOrAfter = (a: Date, b: Date) =>
    a.getFullYear() > b.getFullYear() ||
    (a.getFullYear() === b.getFullYear() && a.getMonth() >= b.getMonth());

// ─── Hook: close a popover when clicking outside it ──────────────────────────

export const usePopoverClose = (
    isOpen: boolean,
    ref: React.RefObject<HTMLDivElement>,
    onClose: () => void,
) => {
    useEffect(() => {
        if (!isOpen) return;
        let cleanup: (() => void) | null = null;
        const timer = setTimeout(() => {
            const handler = (e: MouseEvent) => {
                if (ref.current && !ref.current.contains(e.target as Node)) onClose();
            };
            document.addEventListener('mousedown', handler);
            cleanup = () => document.removeEventListener('mousedown', handler);
        }, 50);
        return () => { clearTimeout(timer); cleanup?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
};

